// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * Context API para gestión de idiomas y traducciones
 * @description Usa lazy loading para cargar traducciones bajo demanda
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  loadTranslations,
  clearTranslationsCache,
  invalidateCache,
  reloadTranslations
} from '../utils/translations/index.js';
import { STORAGE_KEYS, LOGIN_LANGUAGE_OVERRIDE_KEY } from '../utils/constants';
import { SUPPORTED_LANGUAGES_COUNT } from '../constants/i18n';
import { normalizeLang } from '../i18n/langNormalize';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../i18n/supportedLanguages.js';
import { getNestedValue } from '../utils/i18n/getNestedValue';
import { shouldBlockI18nStringResult } from '../utils/i18n/guardI18nKeyLeak';
import socketService from '../services/socketService';
import { getTranslationsSync } from '../utils/translations/index.js';
import enLocaleModule from '../i18n/locales/en.js';
import esLocaleModule from '../i18n/locales/es.js';

const NAMESPACE_KEYS_STATIC = ['common', 'profile', 'plans', 'messages', 'settings'];

/** Árbol anidado para t(); mismo criterio que el useCallback interno del provider. */
function buildRuntimeTranslationsStatic(source) {
  const raw = (source && typeof source === 'object' && !Array.isArray(source)) ? source : {};
  const runtime = {};
  NAMESPACE_KEYS_STATIC.forEach((ns) => {
    runtime[ns] = {};
  });
  Object.entries(raw).forEach(([key, value]) => {
    if (value == null) return;
    const parts = String(key).split('.');
    let cursor = runtime;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const part = parts[i];
      if (
        cursor[part] &&
        (typeof cursor[part] !== 'object' || Array.isArray(cursor[part]))
      ) {
        cursor[part] = { __value: cursor[part] };
      } else if (!cursor[part] || typeof cursor[part] !== 'object' || Array.isArray(cursor[part])) {
        cursor[part] = {};
      }
      cursor = cursor[part];
    }
    const leaf = parts[parts.length - 1];
    if (cursor[leaf] && typeof cursor[leaf] === 'object' && !Array.isArray(cursor[leaf])) {
      cursor[leaf].__value = value;
    } else {
      cursor[leaf] = value;
    }
  });
  return runtime;
}

function pickFlatTranslationString(pack, key) {
  if (!pack || typeof pack !== 'object') return null;
  if (Object.prototype.hasOwnProperty.call(pack, key)) {
    const v = pack[key];
    return typeof v === 'string' ? v : null;
  }
  return null;
}

function bootstrapUiString(key, lang) {
  const L = normalizeLang(lang) || DEFAULT_LANGUAGE;
  if (key === 'common.loading') {
    return L === 'en' ? 'Loading…' : 'Cargando…';
  }
  return '';
}

const INITIAL_RUNTIME_EN = buildRuntimeTranslationsStatic(enLocaleModule);
const INITIAL_RUNTIME_ES = buildRuntimeTranslationsStatic(esLocaleModule);

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[LanguageContext] Provider missing');
    }
    const fallbackLang = DEFAULT_LANGUAGE;
    return {
      currentLanguage: fallbackLang,
      languageReady: true,
      changeLanguage: () => {},
      t: (key) => (key === 'errors.modal.title' ? 'Error' : ''),
      hasKey: () => false,
      keyCount: 0,
      loadingTranslations: false,
      i18nReady: true,
      invalidateAndReload: async () => {},
      clearCache: () => {},
    };
  }
  return context;
};

function getInitialLang() {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const params = new URLSearchParams(window.location.search);
    const qLang = params.get('lang');
    if (qLang) return normalizeLang(qLang);
    const loginOverride = sessionStorage.getItem(LOGIN_LANGUAGE_OVERRIDE_KEY);
    if (loginOverride) return normalizeLang(loginOverride);
    const stored = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    if (stored) return normalizeLang(stored);
    return DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

export const LanguageProvider = ({ children }) => {
  const rtlLanguages = ['ar', 'he'];

  const buildRuntimeTranslations = useCallback(
    (source) => buildRuntimeTranslationsStatic(source),
    []
  );

  const applyDocumentLanguageAttributes = useCallback((lang) => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = lang;
    document.documentElement.dir = rtlLanguages.includes(lang) ? 'rtl' : 'ltr';
  }, [rtlLanguages]);
  const resolveLanguage = (currentUser = null) => {
    if (typeof window !== 'undefined') {
      const override = sessionStorage.getItem(LOGIN_LANGUAGE_OVERRIDE_KEY);
      if (override) {
        const normalized = normalizeLang(override);
        if (SUPPORTED_LANGUAGES.includes(normalized)) {
          return { lang: normalized, source: 'loginOverride' };
        }
      }
      const stored = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
      if (stored) {
        const normalized = normalizeLang(stored);
        if (SUPPORTED_LANGUAGES.includes(normalized)) {
          return { lang: normalized, source: 'localStorage' };
        }
      }
    }
    return { lang: DEFAULT_LANGUAGE, source: 'default' };
  };

  const [currentLanguage, setCurrentLanguage] = useState(getInitialLang);
  /** false hasta fijar idioma en localStorage (evita que el socket haga handshake con 'es' por carrera). */
  const [languageReady, setLanguageReady] = useState(false);
  const [translations, setTranslations] = useState(() => {
    try {
      const lang = getInitialLang();
      const raw = lang === 'es' ? esLocaleModule : enLocaleModule;
      return buildRuntimeTranslationsStatic(raw);
    } catch {
      return INITIAL_RUNTIME_EN;
    }
  });
  const [baseTranslations, setBaseTranslations] = useState(() => INITIAL_RUNTIME_EN);
  const [esTranslations, setEsTranslations] = useState(() => INITIAL_RUNTIME_ES);
  const [version, setVersion] = useState(0);
  const [loadingTranslations, setLoadingTranslations] = useState(true);

  useEffect(() => {
    const initial = getInitialLang();
    setCurrentLanguage(initial);
    try {
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, initial);
    } catch {
      // noop
    }
    applyDocumentLanguageAttributes(initial);
    setLanguageReady(true);
  }, [applyDocumentLanguageAttributes]);

  // Cargar traducciones cuando cambia el idioma o al montar
  useEffect(() => {
    let cancelled = false;
    const forceLoadTranslations = async () => {
      setLoadingTranslations(true);
      try {
        const loadedRaw = await loadTranslations(currentLanguage);
        const fallbackRaw = await loadTranslations('en');
        const spanishRaw = await loadTranslations('es');
        const loaded = buildRuntimeTranslations(loadedRaw);
        const fallback = buildRuntimeTranslations(fallbackRaw);
        const spanish = buildRuntimeTranslations(spanishRaw);
        const finalTranslations =
          loaded && Object.keys(loaded).length > 0
            ? loaded
            : fallback;

        if (!cancelled) {
          setBaseTranslations(fallback);
          setEsTranslations(spanish);
          setTranslations(finalTranslations);
          setVersion((v) => v + 1);
        }
      } catch (error) {
        console.error('❌ [I18N FORCE LOAD] Error cargando traducciones:', error);
        if (!cancelled) {
          setBaseTranslations(INITIAL_RUNTIME_EN);
          setEsTranslations(INITIAL_RUNTIME_ES);
          setTranslations(INITIAL_RUNTIME_EN);
          setVersion((v) => v + 1);
        }
      } finally {
        if (!cancelled) setLoadingTranslations(false);
      }
    };
    forceLoadTranslations();
    return () => { cancelled = true; };
  }, [currentLanguage, buildRuntimeTranslations]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, currentLanguage);
    applyDocumentLanguageAttributes(currentLanguage);
  }, [currentLanguage]);

  /**
   * Función de traducción con fallback explícito y logging
   * @param {string} key - Clave de traducción
   * @param {Object} params - Parámetros opcionales para reemplazar
   * @returns {string} Texto traducido
   */
  // Debe ser falsy para permitir patrones como: t('key') || 'Fallback'
  // y evitar que se renderice "—" en la UI cuando una clave falta.
  const safeTranslations = translations ?? {};
  const safeBaseTranslations = baseTranslations ?? {};
  const safeEsTranslations = esTranslations ?? {};
  const translationsFallback = safeBaseTranslations;
  const applyRuntimeParams = (input, params = {}) => {
    let out = String(input);
    const merged = { count: SUPPORTED_LANGUAGES_COUNT, ...(params || {}) };
    Object.keys(merged).forEach((param) => {
      out = out.replace(`{${param}}`, merged[param]);
    });
    return out;
  };

  const t = (key, paramsOrFallback = {}) => {
    const params = (paramsOrFallback && typeof paramsOrFallback === 'object' && !Array.isArray(paramsOrFallback))
      ? paramsOrFallback
      : {};
    const normalizedKey =
      key === 'message.deleted' ? 'message.deleted_placeholder' : key;

    if (!safeTranslations || Object.keys(safeTranslations).length === 0) {
      const packs = [
        getTranslationsSync(currentLanguage),
        getTranslationsSync('en'),
        getTranslationsSync('es'),
      ];
      for (let i = 0; i < packs.length; i += 1) {
        const p = packs[i];
        const hit = pickFlatTranslationString(p, normalizedKey) ?? pickFlatTranslationString(p, key);
        if (typeof hit === 'string' && hit.trim()) return applyRuntimeParams(hit, params);
      }
      const boot = bootstrapUiString(normalizedKey, currentLanguage);
      return boot || '';
    }

    const currentValue = getNestedValue(safeTranslations, normalizedKey);
    const baseValue = currentValue == null || currentValue === '' ? getNestedValue(translationsFallback, normalizedKey) : currentValue;
    if (
      currentLanguage !== 'en' &&
      (currentValue == null || currentValue === '') &&
      !(baseValue == null || baseValue === '')
    ) {
      console.error('[I18N_EN_LEAK_BLOCKED]', key);
      console.warn('[I18N_LEAK_EN]', key);
    }
    const value = baseValue == null || baseValue === '' ? getNestedValue(safeEsTranslations, normalizedKey) : baseValue;

    if (typeof value === 'object') {
      if (value === null) {
        console.error('[I18N_NULL_OBJECT]', { key, lang: currentLanguage });
        return '';
      }

      if (value.__value) return value.__value;
      if (value.label) return value.label;
      if (value.title) return value.title;

      console.error('[I18N_OBJECT_WITHOUT_VALUE]', {
        key,
        value,
        lang: currentLanguage
      });

      return '';
    }

    if (typeof value !== 'string') {
      console.error('[I18N_NON_STRING_VALUE]', {
        key: normalizedKey,
        value,
        type: typeof value,
        lang: currentLanguage
      });
    }

    const finalValue = value ? applyRuntimeParams(value, params) : null;
    if (!finalValue) {
      console.error('[I18N_MISSING_KEY]', normalizedKey);
      console.warn('[I18N MISSING]', normalizedKey);
      return '';
    }

    // Author: SIVOL® - Aldo Frias - Argentina
    // Fecha: 2026-03-25 (Argentina, hardening pre-release)

    // HARDENING: evitar mostrar claves i18n crudas en UI
    if (typeof finalValue === 'string' && shouldBlockI18nStringResult(finalValue, normalizedKey)) {
      console.warn('[I18N_KEY_VISIBLE_BLOCKED]', normalizedKey, finalValue);
      return '';
    }

    return finalValue;
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
      if (!stored) return;
      const normalized = normalizeLang(stored);
      if (!SUPPORTED_LANGUAGES.includes(normalized)) {
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, DEFAULT_LANGUAGE);
      }
    } catch {
      // noop
    }
  }, []);

  /**
   * Cambia el idioma de la aplicación
   * @param {string} lang - Código del idioma
   */
  const changeLanguage = async (lang) => {
    const normalized = normalizeLang(lang);
    if (!SUPPORTED_LANGUAGES.includes(normalized)) {
      console.warn(`🌐 [LANGUAGE_CONTEXT] Idioma no soportado (fallback ${DEFAULT_LANGUAGE}):`, lang);
      return;
    }
    if (normalized === currentLanguage) {
      return;
    }
    setLoadingTranslations(true);
    setCurrentLanguage(normalized);
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, normalized);
    applyDocumentLanguageAttributes(normalized);
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('lang', normalized);
      window.history.replaceState({}, '', url.toString());
    }
    try {
      socketService.syncLanguageWithServer(normalized);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[LANGUAGE_CONTEXT] syncLanguageWithServer:', err?.message || err);
      }
    }
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(
          new CustomEvent('archat:preferred-language-changed', {
            detail: { from: currentLanguage, to: normalized, ts: Date.now() },
          })
        );
      } catch (_) {
        // noop
      }
      try {
        if (localStorage.getItem('ARCHAT_DEBUG_LANGUAGE_RUNTIME') === '1') {
          console.info('[LANG_RUNTIME_CHANGE]', {
            from: currentLanguage,
            to: normalized,
            socketConnected: !!socketService?.socket?.connected,
            ts: Date.now(),
          });
        }
      } catch (_) {
        // noop
      }
    }
  };

  /**
   * Invalida el caché y recarga las traducciones
   */
  const invalidateAndReload = async () => {
    clearTranslationsCache();
    invalidateCache('en');
    invalidateCache(currentLanguage);
    setLoadingTranslations(true);
    try {
      const enPack = await reloadTranslations('en');
      const esPack = await reloadTranslations('es');
      const reloaded = await reloadTranslations(currentLanguage);
      const nextBase = buildRuntimeTranslations(enPack);
      const nextSpanish = buildRuntimeTranslations(esPack);
      const nextRuntime = buildRuntimeTranslations(reloaded);
      setBaseTranslations(nextBase);
      setEsTranslations(nextSpanish);
      setTranslations(nextRuntime);
    } catch (error) {
      console.error('❌ [LANGUAGE_CONTEXT] Error recargando traducciones:', error);
    } finally {
      setLoadingTranslations(false);
    }
  };

  const i18nReady = !loadingTranslations && safeTranslations && Object.keys(safeTranslations).length > 0;

  const hasKey = (key) => {
    const value = getNestedValue(safeTranslations, key);
    return value != null && value !== '';
  };
  const keyCount = (safeTranslations && typeof safeTranslations === 'object') ? Object.keys(safeTranslations).length : 0;

  // FASE 5 — Registry debug (solo DEV): exponer t y registry para validación en DevTools
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (import.meta.env.DEV) {
      try {
        window.__ARCHAT_I18N__ = {
          t,
          registry: safeTranslations,
          lang: currentLanguage,
          keyCount
        };
      } catch (e) {
        // no-op
      }
    } else if (window.__ARCHAT_I18N__) {
      delete window.__ARCHAT_I18N__;
    }
  }, [currentLanguage, safeTranslations, keyCount]);

  const value = {
    currentLanguage,
    languageReady,
    changeLanguage,
    t,
    translations,
    version,
    hasKey,
    keyCount,
    themeOverrideGuard: 'THEME_OVERRIDE_BLOCKED',
    loadingTranslations,
    i18nReady,
    invalidateAndReload,
    clearCache: clearTranslationsCache
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

